import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Crown,
  ExternalLink,
  KeyRound,
  LogIn,
  Plus,
  RefreshCw,
  UserPlus,
  Users,
  WifiOff,
} from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import { aSlug } from '@/lib/slug';
import { listWorkspaces } from '@/api/workspaces.api';
import { useWorkspaceStore } from '@/store/workspace.store';
import * as clientesApi from '@/api/negocios-cliente.api';
import { ApiError } from '@/api/client';
import type { AccesoDado, ClienteResumen, EstadoConversia, TipoCliente } from '@/api/negocios-cliente.api';

/**
 * LOS CLIENTES DE CONVERSIA — la vista del admin de todo.
 *
 * Conversia es UNA app para todos sus clientes. Cada fila es un negocio y dice
 * qué clase de cliente es:
 *
 *   Nuevo     → vive dentro de esta Conversia.
 *   Reflejo   → un sistema en producción que solo se MUESTRA por su URL.
 *   VIP         Desde acá no se toca, no se copia ni se lee su base.
 *   Pendiente → todavía no está en internet.
 */

const TIPO: Record<TipoCliente, { etiqueta: string; variant: BadgeProps['variant'] }> = {
  nuevo: { etiqueta: 'Nuevo', variant: 'brand' },
  reflejo: { etiqueta: 'Reflejo', variant: 'info' },
  vip: { etiqueta: 'VIP', variant: 'warning' },
  pendiente: { etiqueta: 'Pendiente', variant: 'default' },
};

const QK = ['operacion', 'clientes'] as const;
const QK_ESTADO = ['operacion', 'estado'] as const;

/** Cómo va un Conversia, en pocas palabras y un color (lo que contesta su /health y su /ready). */
export function salud(e: EstadoConversia | undefined): { texto: string; variant: BadgeProps['variant']; problema: boolean } {
  if (!e) return { texto: 'Sin revisar', variant: 'default', problema: false };
  if (!e.vivo) return { texto: 'No contesta', variant: 'danger', problema: true };
  if (!e.canales) return { texto: 'Vivo', variant: 'success', problema: false };
  if (e.canales.total === 0) return { texto: 'Vivo · sin WhatsApp', variant: 'warning', problema: false };
  const caidos = e.canales.total - e.canales.conectados;
  if (caidos === 0) return { texto: `Vivo · ${e.canales.total} de ${e.canales.total} WhatsApp`, variant: 'success', problema: false };
  return { texto: `${caidos} de ${e.canales.total} WhatsApp caído${caidos === 1 ? '' : 's'}`, variant: 'danger', problema: true };
}

export default function Clientes() {
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [altaAbierta, setAltaAbierta] = useState(false);
  const [accesoPara, setAccesoPara] = useState<ClienteResumen | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: QK,
    queryFn: clientesApi.listarClientes,
  });
  const estado = useQuery({ queryKey: QK_ESTADO, queryFn: () => clientesApi.estadoDeTodos(), refetchInterval: 120_000 });
  const [revisando, setRevisando] = useState(false);
  const revisarAhora = async () => {
    setRevisando(true);
    try {
      qc.setQueryData(QK_ESTADO, await clientesApi.estadoDeTodos(true));
    } finally {
      setRevisando(false);
    }
  };
  const estadoDe = (id: string) => estado.data?.estados.find((e) => e.workspaceId === id);

  /**
   * ENTRAR = abrir ese Conversia en otra pestaña YA DENTRO, como su dueño, sin clave (el pase del global).
   * La pestaña se abre en el mismo toque (si se abre después de esperar al servidor, el navegador la
   * bloquea) y luego se le pone la dirección con el pase.
   */
  const entrar = async (c: ClienteResumen) => {
    const pestana = window.open('about:blank', '_blank');
    try {
      const { url } = await clientesApi.pedirPase(c.workspaceId);
      if (pestana) {
        pestana.opener = null;
        pestana.location.href = url;
      } else window.location.assign(url);
    } catch (e) {
      pestana?.close();
      toast.error(`No se pudo entrar a "${c.nombre}"`, e instanceof Error ? e.message : undefined);
    }
  };

  /**
   * ABRIR = poner ese negocio como activo e ir a «Mi Conversia». Igual que el
   * selector de negocios: todo lo cargado era del anterior y se tira entero.
   * Un cliente recién dado de alta todavía no está en la lista del selector:
   * se vuelve a pedir antes, porque el store rechaza ids que no conoce.
   */
  const abrir = async (c: ClienteResumen) => {
    const store = useWorkspaceStore.getState();
    if (!store.items.some((w) => w.id === c.workspaceId)) {
      const lista = await qc.fetchQuery({ queryKey: ['workspaces'], queryFn: listWorkspaces, staleTime: 0 });
      store.setWorkspaces(lista);
    }
    useWorkspaceStore.getState().setActive(c.workspaceId);
    if (useWorkspaceStore.getState().activeId !== c.workspaceId) {
      toast.error(`No se pudo abrir "${c.nombre}": tu cuenta no lo ve en su lista de negocios.`);
      return;
    }
    qc.clear();
    await qc.resetQueries();
    navigate('/mi-conversia');
  };

  const lista = data ?? [];
  const conDireccion = lista.filter((c) => c.programaUrl);
  const cuenta = {
    vivos: conDireccion.filter((c) => estadoDe(c.workspaceId)?.vivo).length,
    caidos: conDireccion.filter((c) => estadoDe(c.workspaceId) && !estadoDe(c.workspaceId)!.vivo).length,
    whatsapp: conDireccion.filter((c) => {
      const e = estadoDe(c.workspaceId);
      return !!e?.vivo && !!e.canales && e.canales.conectados < e.canales.total;
    }).length,
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Todos los Conversia"
        description="Todos nuestros Conversia en un solo lugar, solo para nosotros: cómo va cada uno y un toque para entrar sin clave."
        action={
          <>
            <Button variant="outline" onClick={revisarAhora} disabled={revisando}>
              <RefreshCw className={cn('h-4 w-4', revisando && 'animate-spin')} /> Revisar ahora
            </Button>
            <Button variant="outline" onClick={() => navigate('/negocios/nuevo')}>
              <Building2 className="h-4 w-4" /> Nuevo negocio
            </Button>
            <Button onClick={() => setAltaAbierta(true)}>
              <Plus className="h-4 w-4" /> Cliente externo
            </Button>
          </>
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size={28} className="text-brand-600" />
        </div>
      ) : isError ? (
        <EmptyState
          icon={AlertTriangle}
          title="No se pudo cargar la lista"
          description={(error as Error).message}
          action={
            <Button variant="outline" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      ) : lista.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="Todavía no hay clientes"
          description="Monta un negocio nuevo o registra uno que ya corre en otro lado."
          action={
            <Button onClick={() => setAltaAbierta(true)}>
              <Plus className="h-4 w-4" /> Cliente externo
            </Button>
          }
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="grid grid-cols-3 divide-x divide-surface-border border-b border-surface-border dark:divide-slate-800 dark:border-slate-800">
            {[
              { n: cuenta.vivos, t: 'vivos', c: 'text-emerald-600 dark:text-emerald-400' },
              { n: cuenta.whatsapp, t: 'con WhatsApp caído', c: cuenta.whatsapp ? 'text-red-600 dark:text-red-400' : 'text-slate-400' },
              { n: cuenta.caidos, t: 'no contestan', c: cuenta.caidos ? 'text-red-600 dark:text-red-400' : 'text-slate-400' },
            ].map((x) => (
              <div key={x.t} className="p-4 text-center">
                <div className={cn('text-2xl font-bold tabular-nums', x.c)}>{estado.isLoading ? '…' : x.n}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">{x.t}</div>
              </div>
            ))}
          </div>
          <ul className="divide-y divide-surface-border dark:divide-slate-800">
            {lista.map((c) => (
              <FilaCliente
                key={c.workspaceId}
                c={c}
                estado={estadoDe(c.workspaceId)}
                puedeEntrar={!!estado.data?.puedeEntrar}
                onEntrar={() => entrar(c)}
                onAbrir={() => abrir(c)}
                onAcceso={() => setAccesoPara(c)}
              />
            ))}
          </ul>
        </Card>
      )}

      <ModalAlta open={altaAbierta} onClose={() => setAltaAbierta(false)} />
      {accesoPara && <ModalAcceso cliente={accesoPara} onClose={() => setAccesoPara(null)} />}
    </div>
  );
}

export function FilaCliente({
  c,
  estado,
  puedeEntrar = false,
  onEntrar,
  onAbrir,
  onAcceso,
}: {
  c: ClienteResumen;
  estado?: EstadoConversia;
  puedeEntrar?: boolean;
  onEntrar?: () => void;
  onAbrir: () => void;
  onAcceso: () => void;
}) {
  const tipo = TIPO[c.tipo] ?? TIPO.pendiente;
  const vip = c.tipo === 'vip';
  const s = salud(estado);
  return (
    <li
      className={cn(
        'flex flex-col gap-3 border-l-4 border-l-transparent p-4 sm:flex-row sm:items-center sm:gap-4',
        vip && 'border-l-amber-400 bg-amber-50/40 dark:bg-amber-900/10',
        !c.activo && 'opacity-60',
      )}
    >
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate font-semibold text-slate-900 dark:text-slate-100">{c.nombre}</span>
          <Badge variant={tipo.variant}>
            {vip && <Crown className="h-3 w-3" />}
            {tipo.etiqueta}
          </Badge>
          {!c.activo && <Badge>Apagado</Badge>}
          {c.programaUrl && (
            <Badge variant={s.variant}>
              {estado && !estado.vivo && <WifiOff className="h-3 w-3" />}
              {s.texto}
            </Badge>
          )}
        </div>
        {s.problema && estado && <p className="text-xs text-red-600 dark:text-red-400">{estado.resumen}</p>}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
          <span className="font-mono">{c.slug}</span>
          {c.tipo === 'nuevo' ? (
            <span className="inline-flex items-center gap-1">
              <Users className="h-3.5 w-3.5" /> {c.usuarios} {c.usuarios === 1 ? 'usuario' : 'usuarios'}
            </span>
          ) : (
            c.programaUrl && estado?.vivo && !estado.pase && <span>Falta instalarle la entrada del global</span>
          )}
        </div>
        <p className="min-w-0 text-xs text-slate-600 dark:text-slate-300">
          {c.programaUrl ? (
            <a
              href={c.programaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex max-w-full items-center gap-1 text-brand-700 hover:underline dark:text-brand-300"
            >
              <span className="truncate">{c.programaUrl}</span>
              <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
          ) : c.tipo === 'nuevo' ? (
            c.modulos.length > 0 ? (
              <span>Módulos: {c.modulos.join(', ')}</span>
            ) : (
              <span>Sin módulos encendidos</span>
            )
          ) : (
            <span>Todavía no está en internet</span>
          )}
        </p>
      </div>
      <div className="flex shrink-0 gap-2">
        {/* Tu propia oficina (la del módulo Operación) no se le abre a nadie: el servidor la rechaza igual. */}
        {/* «Dar acceso» solo en los negocios que viven aquí: los demás tienen sus cuentas en su propio Conversia. */}
        {c.tipo === 'nuevo' && !c.modulos.includes('operacion') && (
          <Button size="sm" variant="outline" onClick={onAcceso}>
            <UserPlus className="h-4 w-4" /> Dar acceso
          </Button>
        )}
        {c.programaUrl && puedeEntrar && estado?.pase && onEntrar ? (
          <Button size="sm" onClick={onEntrar}>
            <KeyRound className="h-4 w-4" /> Entrar
          </Button>
        ) : (
          <Button size="sm" variant={c.programaUrl ? 'outline' : undefined} onClick={onAbrir}>
            <LogIn className="h-4 w-4" /> {c.programaUrl ? 'Ver aquí' : 'Abrir'}
          </Button>
        )}
      </div>
    </li>
  );
}

const TIPOS_EXTERNOS = [
  { value: 'reflejo', label: 'Reflejo — ya corre en producción' },
  { value: 'vip', label: 'VIP — ya corre en producción, cliente principal' },
  { value: 'pendiente', label: 'Pendiente — todavía no está en internet' },
];

function ModalAlta({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [nombre, setNombre] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTocado, setSlugTocado] = useState(false);
  const [tipo, setTipo] = useState<'reflejo' | 'vip' | 'pendiente'>('reflejo');
  const [url, setUrl] = useState('');
  const [embebible, setEmbebible] = useState(true);
  const [fallo, setFallo] = useState<string | null>(null);

  const slugEfectivo = slugTocado ? slug : aSlug(nombre);
  const urlObligatoria = tipo !== 'pendiente';

  const cerrar = () => {
    setNombre('');
    setSlug('');
    setSlugTocado(false);
    setTipo('reflejo');
    setUrl('');
    setEmbebible(true);
    setFallo(null);
    onClose();
  };

  const crear = useMutation({
    mutationFn: () =>
      clientesApi.crearClienteExterno({
        nombre: nombre.trim(),
        slug: slugEfectivo,
        tipo,
        programaUrl: url.trim() || undefined,
        embebible,
      }),
    onSuccess: () => {
      toast.success(`"${nombre.trim()}" quedó registrado.`);
      void qc.invalidateQueries({ queryKey: QK });
      void qc.invalidateQueries({ queryKey: ['workspaces'] });
      cerrar();
    },
    onError: (e: Error) => setFallo(e.message),
  });

  const puede =
    nombre.trim().length > 0 && slugEfectivo.length >= 2 && (!urlObligatoria || url.trim().startsWith('https://'));

  return (
    <Modal
      open={open}
      onClose={cerrar}
      title="Cliente externo"
      description="Un sistema que vive fuera de esta Conversia. Solo se guarda su nombre y su dirección pública."
      footer={
        <>
          <Button variant="ghost" onClick={cerrar}>
            Cancelar
          </Button>
          <Button onClick={() => crear.mutate()} disabled={!puede} loading={crear.isPending}>
            Registrar
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input label="Nombre" value={nombre} placeholder="Trendísima" onChange={(e) => setNombre(e.target.value)} />
        <Input
          label="Identificador corto"
          className="font-mono"
          value={slugEfectivo}
          hint="Sale del nombre solo. Minúsculas, números y guiones."
          onChange={(e) => {
            setSlugTocado(true);
            setSlug(aSlug(e.target.value));
          }}
        />
        <Select
          label="Tipo"
          value={tipo}
          options={TIPOS_EXTERNOS}
          onChange={(e) => setTipo(e.target.value as typeof tipo)}
        />
        <Input
          label={urlObligatoria ? 'URL del programa' : 'URL del programa (opcional)'}
          type="url"
          inputMode="url"
          value={url}
          placeholder="https://"
          hint="Solo la dirección pública, con https. Nunca usuario ni clave."
          onChange={(e) => setUrl(e.target.value)}
        />
        <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={embebible}
            onChange={(e) => setEmbebible(e.target.checked)}
          />
          <span>
            Se puede mostrar dentro del panel
            <span className="block text-xs text-slate-500">
              Si su sistema no se deja meter en un marco, se abre en otra pestaña.
            </span>
          </span>
        </label>
        {fallo && <p className="rounded-md bg-red-50 p-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">{fallo}</p>}
      </div>
    </Modal>
  );
}

function ModalAcceso({ cliente, onClose }: { cliente: ClienteResumen; onClose: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [nombre, setNombre] = useState('');
  const [password, setPassword] = useState('');
  const [fallo, setFallo] = useState<string | null>(null);
  // La cuenta ya entra a otros negocios: el servidor pide confirmarlo (409 YA_ENTRA_A_OTROS).
  const [confirmar, setConfirmar] = useState<string | null>(null);
  // La clave se guarda solo para mostrarla UNA vez; al cerrar se va con el modal.
  const [hecho, setHecho] = useState<(AccesoDado & { clave: string | null; email: string }) | null>(null);

  const dar = useMutation({
    mutationFn: (tambienEn: boolean) =>
      clientesApi.darAcceso(cliente.workspaceId, {
        email: email.trim(),
        nombre: nombre.trim() || undefined,
        password: password || undefined,
        ...(tambienEn ? { tambienEn: true } : {}),
      }),
    onSuccess: (r) => {
      setHecho({ ...r, clave: r.usuarioNuevo ? password : null, email: email.trim().toLowerCase() });
      setPassword('');
      setConfirmar(null);
      void qc.invalidateQueries({ queryKey: QK });
    },
    onError: (e: Error) => {
      if (e instanceof ApiError && e.code === 'YA_ENTRA_A_OTROS') {
        setFallo(null);
        setConfirmar(e.message);
      } else {
        setConfirmar(null);
        setFallo(e.message);
      }
    },
  });

  const claveCorta = password.length > 0 && password.length < 8;
  const puede = email.includes('@') && !claveCorta;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Dar acceso a ${cliente.nombre}`}
      description="La persona entra a este cliente como administradora."
      footer={
        hecho ? (
          <Button onClick={onClose}>Listo</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            {confirmar ? (
              <Button onClick={() => dar.mutate(true)} loading={dar.isPending}>
                <KeyRound className="h-4 w-4" /> Sí, también a {cliente.nombre}
              </Button>
            ) : (
              <Button onClick={() => dar.mutate(false)} disabled={!puede} loading={dar.isPending}>
                <KeyRound className="h-4 w-4" /> Dar acceso
              </Button>
            )}
          </>
        )
      }
    >
      {hecho ? (
        <div className="space-y-3 text-sm">
          <p className="flex items-center gap-2 font-medium text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="h-5 w-5" />
            {hecho.usuarioNuevo ? 'Cuenta creada y con acceso.' : 'Acceso dado a una cuenta que ya existía.'}
          </p>
          <p className="text-slate-600 dark:text-slate-300">
            Correo: <span className="font-mono">{hecho.email}</span>
          </p>
          {hecho.clave ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-900/20">
              <p className="text-xs text-amber-800 dark:text-amber-200">
                Clave (se muestra solo esta vez; pásasela por un canal seguro):
              </p>
              <p className="mt-1 select-all break-all font-mono text-base text-slate-900 dark:text-slate-100">
                {hecho.clave}
              </p>
            </div>
          ) : (
            <p className="text-slate-600 dark:text-slate-300">Entra con su propia clave: no se cambió.</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <Input
            label="Correo"
            type="email"
            value={email}
            placeholder="alguien@ejemplo.com"
            onChange={(e) => {
              setEmail(e.target.value);
              setConfirmar(null);
            }}
          />
          <Input label="Nombre (opcional)" value={nombre} onChange={(e) => setNombre(e.target.value)} />
          <Input
            label="Clave (solo si la cuenta es nueva)"
            type="password"
            autoComplete="new-password"
            value={password}
            placeholder="mínimo 8 caracteres"
            error={claveCorta ? 'Mínimo 8 caracteres.' : undefined}
            hint="Si la cuenta ya existe, déjala vacía: conserva su propia clave y aquí no se cambia. Si es nueva, hace falta."
            onChange={(e) => setPassword(e.target.value)}
          />
          {fallo && (
            <p className="rounded-md bg-red-50 p-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">{fallo}</p>
          )}
          {confirmar && (
            <p role="alert" className="rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
              {confirmar} Si es la persona correcta, confirma abajo; si no, revisa el correo.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
